import { useState } from 'react';

import { Button } from '@shared';

import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import { FilePicker } from '@/components/FilePicker';
import { useAuth } from '@/features/auth/useAuth';
import { useLocale, useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi, type OrderFileCategory } from '@/services/api/orders-api';
import { useUploadProgress } from '@/features/upload/useUploadProgress';
import type { UploadProgressOperation } from '@/features/upload/upload-progress-context';

const MAX_FILES_PER_BATCH = 50;
const MAX_FILE_SIZE_BYTES = 300 * 1024 * 1024;
const BLACKLISTED_EXTENSIONS = new Set([
  '.386',
  '.ade',
  '.adp',
  '.app',
  '.appx',
  '.apk',
  '.asax',
  '.ascx',
  '.ashx',
  '.asmx',
  '.asp',
  '.aspx',
  '.bas',
  '.bat',
  '.cab',
  '.chm',
  '.cmd',
  '.com',
  '.cpl',
  '.csh',
  '.dll',
  '.dmg',
  '.dylib',
  '.ear',
  '.elf',
  '.eps',
  '.exe',
  '.gadget',
  '.hta',
  '.htm',
  '.html',
  '.inf',
  '.ins',
  '.iqy',
  '.isp',
  '.jar',
  '.java',
  '.js',
  '.jse',
  '.jsp',
  '.jspx',
  '.ksh',
  '.lnk',
  '.mde',
  '.msc',
  '.msi',
  '.msp',
  '.mst',
  '.ocx',
  '.pif',
  '.pl',
  '.pm',
  '.php',
  '.php3',
  '.php4',
  '.php5',
  '.phtml',
  '.ps',
  '.ps1',
  '.ps1xml',
  '.ps2',
  '.ps2xml',
  '.psc1',
  '.psd1',
  '.psm1',
  '.py',
  '.pyc',
  '.pyw',
  '.rb',
  '.reg',
  '.scf',
  '.scr',
  '.sct',
  '.sh',
  '.shb',
  '.shs',
  '.so',
  '.svg',
  '.svgz',
  '.sys',
  '.url',
  '.vb',
  '.vbe',
  '.vbs',
  '.vxd',
  '.war',
  '.wsc',
  '.wsf',
  '.wsh',
  '.xhtml',
  '.xht',
  '.xla',
  '.xlam',
  '.xll',
  '.xlsm',
  '.xltm',
  '.mht',
  '.mhtml',
  '.docm',
  '.dotm',
  '.pptm',
  '.potm',
  '.ppam',
  '.ppsm',
  '.sldm',
]);

interface AdditionalFilesUploadProps {
  orderId: string;
  enabled: boolean;
  onUploaded: () => Promise<void> | void;
}

function isBlacklistedFile(file: File): boolean {
  return file.name
    .split('.')
    .filter(Boolean)
    .some((part) => BLACKLISTED_EXTENSIONS.has(`.${part.toLowerCase()}`));
}

function formatFileSize(bytes: number): string {
  if (bytes <= 1024 * 1024) {
    return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(bytes / 1024)} KB`;
  }

  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

export function AdditionalFilesUpload({ orderId, enabled, onUploaded }: AdditionalFilesUploadProps) {
  const locale = useLocale();
  const t = useTranslations('common.orderDetails');
  const tErrors = useTranslations('errors.api');
  const { client } = useAuth();
  const { beginUpload } = useUploadProgress();
  const [selectedAdditionalFiles, setSelectedAdditionalFiles] = useState<File[]>([]);
  const [selectedProtocolFiles, setSelectedProtocolFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [processingFileName, setProcessingFileName] = useState<string | null>(null);
  const [failedFileName, setFailedFileName] = useState<string | null>(null);

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>, category: OrderFileCategory) => {
    const pickedFiles = Array.from(event.target.files ?? []);
    event.target.value = '';

    if (pickedFiles.length === 0) {
      return;
    }

    const selectedFiles = category === 'OtherData' ? selectedAdditionalFiles : selectedProtocolFiles;
    const otherSelectedFiles = category === 'OtherData' ? selectedProtocolFiles : selectedAdditionalFiles;
    const nextFiles = [...selectedFiles];
    let nextValidationMessage: string | null = null;

    for (const file of pickedFiles) {
      if (nextFiles.length + otherSelectedFiles.length >= MAX_FILES_PER_BATCH) {
        nextValidationMessage = t('additionalFilesSection.tooMany');
        break;
      }

      if (isBlacklistedFile(file)) {
        nextValidationMessage = t('additionalFilesSection.blockedExtension', { file: file.name });
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextValidationMessage = t('additionalFilesSection.tooLarge', { file: file.name });
        continue;
      }

      const alreadySelected = [...nextFiles, ...otherSelectedFiles].some(
        (selectedFile) =>
          selectedFile.name === file.name &&
          selectedFile.size === file.size &&
          selectedFile.lastModified === file.lastModified,
      );
      if (!alreadySelected) {
        nextFiles.push(file);
      }
    }

    if (category === 'OtherData') {
      setSelectedAdditionalFiles(nextFiles);
    } else {
      setSelectedProtocolFiles(nextFiles);
    }
    setValidationMessage(nextValidationMessage);
    setErrorKey(null);
    setFailedFileName(null);
    setSuccessMessage(null);
  };

  const handleRemove = (fileToRemove: File, category: OrderFileCategory) => {
    if (category === 'OtherData') {
      setSelectedAdditionalFiles((files) => files.filter((file) => file !== fileToRemove));
    } else {
      setSelectedProtocolFiles((files) => files.filter((file) => file !== fileToRemove));
    }
    setValidationMessage(null);
    setFailedFileName(null);
    setSuccessMessage(null);
  };

  const renderFileList = (files: readonly File[], category: OrderFileCategory) =>
    files.length > 0 ? (
      <ul className="mt-4 m-0 flex max-h-48 list-none flex-col gap-1 overflow-y-auto p-0">
        {files.map((file) => (
          <li
            key={`${category}-${file.name}-${file.size}-${file.lastModified}`}
            className="flex min-h-8 min-w-0 items-center justify-between gap-2 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-2.5 py-1.5"
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <strong className="min-w-0 truncate text-xs text-[var(--md-primary)]">{file.name}</strong>
              {processingFileName === file.name ? (
                <ArrowPathIcon
                  className="h-4 w-4 shrink-0 animate-spin text-[var(--md-primary)]"
                  aria-label={t('additionalFilesSection.processingFile')}
                />
              ) : null}
              <span className="shrink-0 text-[0.6875rem] text-[var(--zs-muted)]">
                {formatFileSize(file.size)}
              </span>
            </span>
            <button
              type="button"
              className="shrink-0 whitespace-nowrap text-xs font-bold text-[var(--md-primary)] underline"
              disabled={isUploading}
              onClick={() => handleRemove(file, category)}
            >
              {t('additionalFilesSection.remove')}
            </button>
          </li>
        ))}
      </ul>
    ) : null;

  const handleUpload = async () => {
    const filesToUpload = [
      ...selectedAdditionalFiles.map((file) => ({ file, category: 'OtherData' as const })),
      ...selectedProtocolFiles.map((file) => ({ file, category: 'ProtocolData' as const })),
    ];

    if (!client || !orderId || filesToUpload.length === 0 || isUploading) {
      if (filesToUpload.length === 0) {
        setValidationMessage(t('additionalFilesSection.empty'));
      }
      return;
    }

    setIsUploading(true);
    setErrorKey(null);
    setValidationMessage(null);
    setSuccessMessage(null);
    setProcessingFileName(null);
    setFailedFileName(null);

    let operationFileName: string | null = null;
    let uploadProgress: UploadProgressOperation | null = null;
    let uploadedFileCount = 0;
    try {
      uploadProgress = beginUpload(0);
      if (!uploadProgress) {
        return;
      }

      const api = new OrdersApi(client);
      const preparedFiles = await api.prepareAdditionalOrderFiles({
        orderId,
        files: filesToUpload.map(({ file, category }) => ({ file, category })),
      });
      if (preparedFiles.length !== filesToUpload.length) {
        setFailedFileName(
          filesToUpload[preparedFiles.length]?.file.name ?? filesToUpload[0]?.file.name ?? null,
        );
        setErrorKey('generic');
        return;
      }

      const requiredUploads = preparedFiles.filter(
        (preparedFile) => preparedFile.uploadRequired !== false,
      ).length;
      uploadProgress.setTotalFiles(requiredUploads);
      if (requiredUploads === 0) {
        uploadProgress.finish();
      }

      for (let index = 0; index < filesToUpload.length; index += 1) {
        const uploadItem = filesToUpload[index];
        const preparedFile = preparedFiles[index];
        if (!uploadItem || !preparedFile?.id) {
          setFailedFileName(operationFileName);
          setErrorKey('generic');
          return;
        }

        const file = uploadItem.file;
        if (preparedFile.uploadRequired !== false) {
          operationFileName = file.name;
          setProcessingFileName(operationFileName);
          uploadProgress.startFile(file.name);
          await api.uploadAdditionalOrderFile(preparedFile.id, file, {
            sendNotification: true,
            message,
            language: locale,
            onProgress: (percentage) => uploadProgress?.setFileProgress(percentage),
          });
          uploadProgress.completeFile();
          uploadedFileCount += 1;
        }
        operationFileName = null;
        setProcessingFileName(null);
        if (uploadItem.category === 'OtherData') {
          setSelectedAdditionalFiles((remainingFiles) =>
            remainingFiles.filter((remainingFile) => remainingFile !== file),
          );
        } else {
          setSelectedProtocolFiles((remainingFiles) =>
            remainingFiles.filter((remainingFile) => remainingFile !== file),
          );
        }
      }

      operationFileName = null;
      await onUploaded();
      if (uploadedFileCount === 0) {
        setSuccessMessage(t('additionalFilesSection.alreadyUploaded'));
      } else if (uploadedFileCount < filesToUpload.length) {
        setSuccessMessage(t('additionalFilesSection.partialSuccess'));
        setMessage('');
      } else {
        setSuccessMessage(t('additionalFilesSection.success'));
        setMessage('');
      }
    } catch (error) {
      setFailedFileName(operationFileName);
      setErrorKey(errorKeyOf(error));
    } finally {
      uploadProgress?.finish();
      setProcessingFileName(null);
      setIsUploading(false);
    }
  };

  return (
    <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2>{t('additionalFilesSection.title')}</h2>
          <p className="m-0 text-[var(--zs-muted)]">{t('additionalFilesSection.description')}</p>
        </div>
        {enabled ? (
          <span className="rounded-full bg-[var(--md-primary-light)] px-2.5 py-1 text-xs font-bold text-[var(--md-primary)]">
            {t('additionalFilesSection.additionalBadge')}
          </span>
        ) : null}
      </div>

      {!enabled ? (
        <p className="mt-4 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] p-4 text-[var(--zs-muted)]">
          {t('additionalFilesSection.closed')}
        </p>
      ) : (
        <>
          <p className="mt-4 text-sm text-[var(--zs-muted)]">{t('additionalFilesSection.limitHint')}</p>
          <div className="mt-4 grid gap-5 lg:grid-cols-2">
            <div>
              <FilePicker
                label={t('additionalFilesSection.additionalPickerTitle')}
                buttonText={t('additionalFilesSection.chooseFiles')}
                selectedFiles={selectedAdditionalFiles}
                selectedFilesLabel={t('additionalFilesSection.selectedCount', {
                  count: selectedAdditionalFiles.length,
                })}
                multiple
                disabled={isUploading}
                onChange={(event) => handleFileSelection(event, 'OtherData')}
              />
              {renderFileList(selectedAdditionalFiles, 'OtherData')}
            </div>

            <div>
              <FilePicker
                label={t('additionalFilesSection.protocolPickerTitle')}
                buttonText={t('additionalFilesSection.protocolChooseFiles')}
                selectedFiles={selectedProtocolFiles}
                selectedFilesLabel={t('additionalFilesSection.selectedCount', {
                  count: selectedProtocolFiles.length,
                })}
                multiple
                disabled={isUploading}
                onChange={(event) => handleFileSelection(event, 'ProtocolData')}
              />
              {renderFileList(selectedProtocolFiles, 'ProtocolData')}
            </div>
          </div>

          {processingFileName ? (
            <p
              className="mt-3 flex items-center gap-2 text-sm text-[var(--zs-muted)]"
              role="status"
              aria-live="polite"
            >
              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('additionalFilesSection.uploadingFile', { file: processingFileName })}
            </p>
          ) : null}

          <div className="mt-5">
            <label
              className="mb-1.5 block text-sm font-bold text-[var(--md-primary)]"
              htmlFor="additional-file-message"
            >
              {t('additionalFilesSection.messageLabel')}
            </label>
            <textarea
              id="additional-file-message"
              className="min-h-24 w-full resize-y rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] px-3 py-2.5 text-sm text-[var(--zs-text)] outline-none focus:border-[var(--md-highlight)]"
              maxLength={4000}
              placeholder={t('additionalFilesSection.messagePlaceholder')}
              value={message}
              disabled={isUploading}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>

          {validationMessage ? (
            <p className="mt-3 text-sm text-[var(--md-highlight)]">{validationMessage}</p>
          ) : null}
          {errorKey ? (
            <p className="mt-3 text-sm text-[var(--md-highlight)]" role="alert">
              {failedFileName ? `${t('additionalFilesSection.fileFailed', { file: failedFileName })} ` : null}
              {tErrors(errorKey)}
            </p>
          ) : null}
          {successMessage ? (
            <div
              className="mt-4 flex items-start gap-3 rounded-[var(--zs-radius-s)] border-l-4 border-[var(--md-primary)] bg-[var(--md-primary-light)] p-4 text-sm text-[var(--md-primary)] shadow-[var(--md-shadow-soft)]"
              role="status"
              aria-live="polite"
            >
              <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <p className="m-0 font-semibold leading-5">{successMessage}</p>
            </div>
          ) : null}

          <Button
            className="mt-4"
            disabled={selectedAdditionalFiles.length + selectedProtocolFiles.length === 0}
            loading={isUploading}
            loadingLabel={t('additionalFilesSection.uploading')}
            onClick={() => void handleUpload()}
          >
            {t('additionalFilesSection.uploadAction')}
          </Button>
        </>
      )}
    </section>
  );
}
